import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';
import { useGetEtlPlatformReconciliationQuery } from 'src/redux/operations/operations.endpoints';
import type { EtlHealthStatusFilter } from '../types/operations.types';
import { formatNumber, formatPercent } from '../helpers/etl-health.helpers';
import { EtlDetailTableV2 } from './etl-detail-table-v2';
import { EtlHealthStatusPillV2 } from './etl-health-status-pill-v2';
import { EtlRunsPagination } from './etl-runs-pagination';

const HEALTH_DAYS = 30;
export const PLATFORM_RECONCILIATION_PAGE_SIZE = 10;

type Props = {
  selectedDate: string | null;
  refreshToken?: number;
};

export function PlatformReconciliationPanelV2({ selectedDate, refreshToken = 0 }: Props) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<EtlHealthStatusFilter>('issues');

  useEffect(() => {
    setPage(1);
  }, [selectedDate, statusFilter]);

  const reconciliationQuery = useGetEtlPlatformReconciliationQuery({
    days: HEALTH_DAYS,
    page,
    page_size: PLATFORM_RECONCILIATION_PAGE_SIZE,
    status_filter: statusFilter,
    ...(selectedDate ? { date: selectedDate } : {}),
    ...(refreshToken > 0 ? { refresh: 1 as const } : {}),
  });

  const rows = reconciliationQuery.data?.rows ?? [];
  const totalCount = reconciliationQuery.data?.total_count ?? 0;
  const totalPages = reconciliationQuery.data?.total_pages ?? 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className={portalDs.filterLabel}>Show</span>
          <select
            className={cn(portalDs.select, 'min-w-[160px]')}
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as EtlHealthStatusFilter);
            }}
          >
            <option value="issues">Warnings and failures</option>
            <option value="all">All statuses</option>
          </select>
        </label>
      </div>

      {(reconciliationQuery.isLoading || reconciliationQuery.isFetching) && (
        <div className={cn('flex items-center gap-2 py-4', portalDs.bodySm)} role="status">
          <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
          Loading platform reconciliation checks…
        </div>
      )}

      {reconciliationQuery.isError && !reconciliationQuery.isLoading && (
        <p className={cn(portalDs.bodySm, 'text-[#B91C1C]')}>
          Could not load platform reconciliation checks.
        </p>
      )}

      {!reconciliationQuery.isLoading && !reconciliationQuery.isError && (
        <>
          <EtlDetailTableV2
            rows={rows}
            columns={[
              { id: 'date', header: 'Date', accessor: (row) => row.check_date },
              { id: 'platform', header: 'Platform', accessor: (row) => row.platform },
              {
                id: 'account',
                header: 'Account',
                accessor: (row) => row.account_name ?? row.account_id,
              },
              { id: 'metric', header: 'Metric', accessor: (row) => row.metric },
              {
                id: 'vendor_total',
                header: 'Vendor total',
                accessor: (row) => row.vendor_total,
                align: 'right',
                render: (row) => formatNumber(row.vendor_total),
              },
              {
                id: 'mart_total',
                header: 'Mart total',
                accessor: (row) => row.mart_total,
                align: 'right',
                render: (row) => formatNumber(row.mart_total),
              },
              {
                id: 'delta_pct',
                header: 'Delta %',
                accessor: (row) => row.delta_pct,
                align: 'right',
                render: (row) => formatPercent(row.delta_pct),
              },
              {
                id: 'threshold_pct',
                header: 'Threshold %',
                accessor: (row) => row.threshold_pct,
                align: 'right',
                render: (row) => formatPercent(row.threshold_pct),
              },
              {
                id: 'status',
                header: 'Status',
                accessor: (row) => row.status,
                render: (row) => <EtlHealthStatusPillV2 status={row.status} />,
              },
              { id: 'notes', header: 'Notes', accessor: (row) => row.notes ?? '—' },
            ]}
            emptyMessage={
              statusFilter === 'issues'
                ? 'No warnings or failures for the selected period.'
                : 'No platform reconciliation checks for the selected period.'
            }
            hideRowCount
          />

          <EtlRunsPagination
            page={page}
            totalPages={totalPages}
            totalObjects={totalCount}
            pageSize={PLATFORM_RECONCILIATION_PAGE_SIZE}
            onPageChange={setPage}
            disabled={reconciliationQuery.isFetching}
          />
        </>
      )}
    </div>
  );
}
